import type { FlatSurface, PlacementPosition } from '@repo/api-contracts';
import { type PointerEvent, type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { clientToPhotoPoint, type PhotoDimensions, surfacePhotoToMm } from '@/entities/edit-area';
import { anchoredClient, type SvgAnchor, svgAnchorOf } from './svg-drag-anchor';

interface Options {
  svgRef: RefObject<SVGSVGElement | null>;
  photoSize: PhotoDimensions;
  disabled: boolean;
  /** 놓은 이동(mm). 반영하면 null, 거부되면 사유(구멍은 그대로). */
  onMove: (componentId: string, index: number, dxMm: number, dyMm: number) => string | null;
}

/** 끄는 중인 구멍과 1mm 로 맞춘 이동량. */
export interface HoleMovePreview {
  readonly componentId: string;
  readonly index: number;
  readonly dxMm: number;
  readonly dyMm: number;
}

interface Drag {
  readonly pointerId: number;
  readonly componentId: string;
  readonly index: number;
  readonly surface: FlatSurface;
  /** 잡은 점의 표면 mm. */
  readonly origin: PlacementPosition;
  readonly client: { readonly x: number; readonly y: number };
  /** 누른 때 svg 의 화면 위치 — 끄는 동안 사진 칸이 움직여도 화면에서 끈 거리만 쓴다. */
  readonly anchor: SvgAnchor | null;
  /** 옮기지 않고 놓았을 때(누르기만 함) 부를 동작. */
  readonly onClick: (() => void) | undefined;
}

/** 누르기만 한 것으로 보는 포인터 흔들림(화면 px). */
const CLICK_SLOP_PX = 3;

/**
 * 고른 인쇄 제외 영역(구멍)을 끌어 옮긴다. 끄는 동안의 이동량은 이 훅의 로컬 미리보기이고, 놓을 때 한 번 반영한다.
 * 이동량은 인쇄 영역 끌기와 같이 1mm 로 맞춘다. 거의 움직이지 않고 놓으면(선택만 한 누르기) 반영하지 않고 onClick 을 부른다.
 */
export const useHoleMovePointer = ({ svgRef, photoSize, disabled, onMove }: Options) => {
  const drag = useRef<Drag | null>(null);
  const [preview, setPreview] = useState<HoleMovePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cancel = useCallback((): void => {
    const pointerId = drag.current?.pointerId;
    drag.current = null;
    setPreview(null);
    if (pointerId !== undefined && svgRef.current?.hasPointerCapture(pointerId))
      svgRef.current.releasePointerCapture(pointerId);
  }, [svgRef]);
  const dismiss = useCallback((): void => setError(null), []);
  useEffect(() => {
    if (disabled) cancel();
  }, [disabled, cancel]);

  const mmOf = (
    surface: FlatSurface,
    event: PointerEvent<SVGElement>,
    anchor: SvgAnchor | null,
  ): PlacementPosition | null => {
    if (svgRef.current === null) return null;
    const client = { x: event.clientX, y: event.clientY };
    const point = clientToPhotoPoint(
      svgRef.current,
      anchor === null ? client : anchoredClient(svgRef.current, anchor, client),
    );
    return point === null ? null : surfacePhotoToMm(surface, photoSize, point);
  };
  const previewAt = (event: PointerEvent<SVGElement>): HoleMovePreview | null => {
    const current = drag.current;
    if (current === null || event.pointerId !== current.pointerId) return null;
    const to = mmOf(current.surface, event, current.anchor);
    if (to === null) return null;
    return {
      componentId: current.componentId,
      index: current.index,
      dxMm: Math.round(to.xMm - current.origin.xMm) + 0,
      dyMm: Math.round(to.yMm - current.origin.yMm) + 0,
    };
  };

  const start = (
    event: PointerEvent<SVGElement>,
    componentId: string,
    index: number,
    surface: FlatSurface,
    onClick?: () => void,
  ): void => {
    if (disabled || event.button !== 0 || svgRef.current === null) return;
    if (photoSize.widthPx <= 0 || photoSize.heightPx <= 0) return;
    const origin = mmOf(surface, event, null);
    if (origin === null) return;
    event.preventDefault();
    drag.current = {
      pointerId: event.pointerId,
      componentId,
      index,
      surface,
      origin,
      client: { x: event.clientX, y: event.clientY },
      anchor: svgAnchorOf(svgRef.current),
      onClick,
    };
    setError(null);
    svgRef.current.setPointerCapture(event.pointerId);
  };
  const move = (event: PointerEvent<SVGElement>): void => {
    const next = previewAt(event);
    if (next !== null) setPreview(next);
  };
  const finish = (event: PointerEvent<SVGElement>): void => {
    const current = drag.current;
    if (current === null || event.pointerId !== current.pointerId) return;
    const next = previewAt(event) ?? preview;
    cancel();
    const clicked =
      Math.hypot(event.clientX - current.client.x, event.clientY - current.client.y) <=
      CLICK_SLOP_PX;
    if (clicked) {
      current.onClick?.();
      return;
    }
    if (next === null || (next.dxMm === 0 && next.dyMm === 0)) return;
    setError(onMove(next.componentId, next.index, next.dxMm, next.dyMm));
  };
  return { preview, error, start, move, finish, cancel, dismiss };
};
